import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import type {
  GetTransactionsByRelatedDriverResponse,
  TransactionCategory,
} from "@qivia/schemas-cards-transactions";
import {
  dateFormatterHourMinute,
  dateFormatterWeekDayDayMonth,
  dateFormatterWeekDayDayMonthYear,
  FilterChips,
  formatAmount,
  IconCircle,
  List,
  ListRow,
  Spacer,
  TextCapitalized,
} from "@qivia/ui";
import { StyledColumn } from "@qivia/ui/src/designSystem/components/driverAppV2/layout";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import {
  CreditCardOutlined,
  EvStationOutlined,
  LocalCarWashOutlined,
  LocalGasStationOutlined,
  LocalParkingOutlined,
  MaintenanceOutlined,
  TollOutlined,
} from "@qivia/ui/src/designSystem/materialUi/materialUi";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectCompanyUuid } from "../../qrCode/vehicleLoginSlice";
import {
  getDriverTransactionsAsync,
  selectTransactions,
  selectTransactionsStatus,
} from "./transactionsSlice";

const CATEGORY_ICONS: Record<TransactionCategory, JSX.Element> = {
  FUEL_AUTOMATON: <LocalGasStationOutlined />,
  SERVICE_STATION: <LocalGasStationOutlined />,
  ELECTRIC: <EvStationOutlined />,
  WASH: <LocalCarWashOutlined />,
  TOLL: <TollOutlined />,
  PARKING: <LocalParkingOutlined />,
  MAINTENANCE: <MaintenanceOutlined />,
  FREIGHT_FORWARDER_AND_CUSTOMS: <CreditCardOutlined />,
  OTHER: <CreditCardOutlined />,
};

const QUANTITY_UNITS: Partial<Record<TransactionCategory, string>> = {
  FUEL_AUTOMATON: "L",
  SERVICE_STATION: "L",
  ELECTRIC: "kWh",
};

type Filter = { key: string; categories: TransactionCategory[] | null };

const FILTERS: Filter[] = [
  { key: "all", categories: null },
  { key: "fuel", categories: ["FUEL_AUTOMATON", "SERVICE_STATION"] },
  { key: "electric", categories: ["ELECTRIC"] },
  { key: "wash", categories: ["WASH"] },
  { key: "toll", categories: ["TOLL"] },
  { key: "parking", categories: ["PARKING"] },
];

type TransactionsDay = {
  key: string;
  date: Date;
  transactions: GetTransactionsByRelatedDriverResponse;
};

export const OperationsHistory = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const companyUuid = useAppSelector(selectCompanyUuid);
  const transactions = useAppSelector(selectTransactions);
  const transactionsStatus = useAppSelector(selectTransactionsStatus);
  const [selectedFilter, setSelectedFilter] = useState(
    t("appV2.operations.filters.all"),
  );

  useEffect(() => {
    if (transactionsStatus === "idle" && companyUuid) {
      void dispatch(getDriverTransactionsAsync({ companyUuid }));
    }
  }, [transactionsStatus, companyUuid, dispatch]);

  const filters = useMemo(
    () =>
      FILTERS.filter(
        (filter) =>
          filter.categories === null ||
          transactions.some((transaction) =>
            filter.categories?.includes(transaction.category),
          ),
      ),
    [transactions],
  );

  const days = useMemo(() => {
    const categories = filters.find(
      (filter) =>
        t(`appV2.operations.filters.${filter.key}`) === selectedFilter,
    )?.categories;
    return transactions
      .filter(
        (transaction) =>
          !categories || categories.includes(transaction.category),
      )
      .reduce<TransactionsDay[]>((acc, transaction) => {
        const date = new Date(transaction.executionDate);
        const key = date.toDateString();
        const day = acc.find((current) => current.key === key);
        if (day) day.transactions.push(transaction);
        else acc.push({ key, date, transactions: [transaction] });
        return acc;
      }, []);
  }, [filters, selectedFilter, t, transactions]);

  return (
    <StyledColumn>
      <FilterChips
        options={filters.map((filter) =>
          t(`appV2.operations.filters.${filter.key}`),
        )}
        selected={selectedFilter}
        onSelect={setSelectedFilter}
      />
      {days.map((day) => (
        <Fragment key={day.key}>
          <Spacer y={1.5} />
          <List
            title={
              <TextCapitalized>
                {day.date.getFullYear() === new Date().getFullYear()
                  ? dateFormatterWeekDayDayMonth(day.date)
                  : dateFormatterWeekDayDayMonthYear(day.date)}
              </TextCapitalized>
            }
            rows={day.transactions.map((transaction) => {
              const unit = QUANTITY_UNITS[transaction.category];
              const time = dateFormatterHourMinute(
                new Date(transaction.executionDate),
              );
              return (
                <ListRow
                  key={transaction.uuid}
                  leftIcon={
                    <IconCircle icon={CATEGORY_ICONS[transaction.category]} />
                  }
                  title={t(
                    `driverPayment.cardExpenses.type.${transaction.category}`,
                  )}
                  subtitle={
                    unit && transaction.quantity !== null
                      ? `${formatAmount(transaction.quantity)} ${unit} · ${time}`
                      : time
                  }
                  rightElement={
                    <StyledAmount>
                      {`${transaction.type === "REFUND" ? "+" : "-"} ${formatAmount(transaction.amount / 100)} €`}
                    </StyledAmount>
                  }
                />
              );
            })}
          />
        </Fragment>
      ))}
      <Spacer y={1.5} />
    </StyledColumn>
  );
};

const StyledAmount = styled.span`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]};
`;
